/**
 * 💬 문의하기 (고객지원) — 2026-07-27
 *
 * 배경: 점주 문의가 전부 카카오톡으로 왔다. 앱엔 '🐛 오류 제보'가 있었지만
 *   "오류"라고 확신해야 누를 수 있는 이름이라 대부분의 '질문'을 담지 못했다.
 *   → 편하게 물어보는 채팅으로. 매장·화면·앱버전은 자동으로 붙여 보낸다.
 *
 * 설계 결정: 읽음/입력중 표시를 만들지 않는다. 1인 본부에 즉답 압박을 만들면
 *   카톡보다 오히려 불만이 커진다. 대신 '보통 1영업일 내 답변'을 명시한다.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '../../../config/api';
import SupportAttachmentImage from '../../../components/common/SupportAttachmentImage';

interface Attachment {
  id: string;
  filename: string | null;
  mime: string | null;
  size: number | null;
}

interface Message {
  id: string;
  author_type: 'owner' | 'hq';
  author_name: string | null;
  content: string;
  category: string | null;
  created_at: string | null;
  attachments: Attachment[];
}

const CATEGORIES = ['질문', '오류', '건의'] as const;

const getToken = () =>
  localStorage.getItem('auth_access_token') ||
  localStorage.getItem('accessToken') ||
  localStorage.getItem('token');

const apiUrl = (path: string) => `${API_BASE_URL === '' ? '/api' : API_BASE_URL}${path}`;

// 첨부 사진은 인증이 필요해 blob 으로 받는다 — 받는 시점은 SupportAttachmentImage 가 정한다
const fetchAttachmentUrl = async (id: string): Promise<string | null> => {
  try {
    const res = await fetch(apiUrl(`/v1/owner/support/attachments/${id}`), {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (!res.ok) return null;
    return URL.createObjectURL(await res.blob());
  } catch {
    return null;
  }
};

const formatTime = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('ko-KR', {
    month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

// 서버가 응답하지 않을 때 화면이 '전송중'에 영영 갇히지 않도록 시간제한을 둔다
const fetchWithTimeout = async (url: string, options: RequestInit, ms: number) => {
  const ac = new AbortController();
  const timer = setTimeout(() => ac.abort(), ms);
  try {
    return await fetch(url, { ...options, signal: ac.signal });
  } finally {
    clearTimeout(timer);
  }
};

const SEND_TIMEOUT_MS = 30000;
// 폴링 주기(30초)보다 짧아야 응답 없는 요청이 뒤로 쌓이지 않는다
const LOAD_TIMEOUT_MS = 20000;

const SupportChatPage: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [category, setCategory] = useState<string>('질문');
  const [files, setFiles] = useState<File[]>([]);
  const bottomRef = useRef<HTMLDivElement>(null);

  const loadThread = useCallback(async () => {
    try {
      const res = await fetchWithTimeout(
        apiUrl('/v1/owner/support/thread'),
        { headers: { Authorization: `Bearer ${getToken()}` } },
        LOAD_TIMEOUT_MS,
      );
      if (!res.ok) throw new Error(`대화를 불러오지 못했습니다 (${res.status})`);
      const data = await res.json();
      setMessages(data.messages || []);
    } catch (e: any) {
      setError(
        e?.name === 'AbortError'
          ? '대화를 불러오는 데 시간이 오래 걸립니다. 잠시 뒤 다시 시도됩니다.'
          : e.message,
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadThread();
    // 답변이 오면 보이도록 가볍게 폴링 (30초) — 실시간 압박 없이 최신화만
    const t = setInterval(loadThread, 30000);
    return () => clearInterval(t);
  }, [loadThread]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  // 첨부 사진은 보일 때만 받는다 (2026-10-14) — 종전엔 대화를 열면 지난 사진을 전부 받았다

  // 스크린샷은 대부분 '캡처 → 바로 붙여넣기' 로 온다. 파일 선택만 있으면
  // 저장→찾기 단계가 생겨 안 쓰게 되므로 붙여넣기·드래그도 받는다. (2026-07-27)
  const addImages = (incoming: File[]) => {
    const imgs = incoming.filter((f) => f.type.startsWith('image/'));
    if (imgs.length === 0) return;
    setFiles((prev) => [...prev, ...imgs].slice(0, 5));
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const items = Array.from(e.clipboardData?.items || []);
    const pasted = items
      .filter((it) => it.kind === 'file' && it.type.startsWith('image/'))
      .map((it, i) => {
        const f = it.getAsFile();
        if (!f) return null;
        // 클립보드 이미지는 이름이 없거나 'image.png' 로 겹친다 → 구분되게 재명명
        return new File([f], f.name && f.name !== 'image.png' ? f.name : `화면캡처_${Date.now()}_${i + 1}.png`, {
          type: f.type,
        });
      })
      .filter((f): f is File => f !== null);
    if (pasted.length > 0) {
      e.preventDefault();
      addImages(pasted);
    }
  };

  const send = async () => {
    const content = text.trim();
    if (!content || sending) return;
    setSending(true);
    setError(null);
    try {
      const form = new FormData();
      form.append('content', content);
      form.append('category', category);
      form.append('page_url', window.location.href);
      form.append(
        'app_context',
        JSON.stringify({
          userAgent: navigator.userAgent,
          screen: `${window.innerWidth}x${window.innerHeight}`,
        }),
      );
      files.forEach((f) => form.append('files', f));

      const res = await fetchWithTimeout(
        apiUrl('/v1/owner/support/messages'),
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${getToken()}` },
          body: form,
        },
        SEND_TIMEOUT_MS,
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `전송 실패 (${res.status})`);
      }
      setText('');
      setFiles([]);
    } catch (e: any) {
      // 시간이 넘어 끊은 경우 서버에 저장됐는지 알 수 없다 — 쓴 글은 지우지 않고,
      // 아래에서 대화를 새로 불러와 실제로 올라갔는지 직접 보게 한다
      setError(
        e?.name === 'AbortError'
          ? '서버 응답이 없어 30초 만에 끊었습니다. 아래 대화에 방금 쓴 글이 보이면 이미 접수된 것이니 다시 보내지 마세요.'
          : e.message || '전송 중 오류가 발생했습니다.',
      );
    } finally {
      setSending(false);
    }
    // 버튼을 먼저 풀고 목록을 불러온다 — 목록이 느려도 '전송중'에 갇히지 않는다
    loadThread();
  };

  return (
    <div className="p-6 max-w-3xl mx-auto flex flex-col" style={{ height: 'calc(100vh - 80px)' }}>
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900">💬 문의하기</h1>
        <p className="text-sm text-gray-500 mt-1">
          궁금한 점, 불편한 점 무엇이든 편하게 남겨주세요. 보통 <b>1영업일 안에</b> 답변드립니다.
        </p>
      </div>

      {/* 대화 영역 */}
      <div className="flex-1 overflow-y-auto bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
        {loading && <div className="text-center text-gray-400 py-8">불러오는 중...</div>}

        {!loading && messages.length === 0 && (
          <div className="text-center text-gray-500 py-12">
            <div className="text-4xl mb-3">👋</div>
            <p className="font-medium text-gray-700">아직 주고받은 대화가 없습니다.</p>
            <p className="text-sm mt-1">
              화면이 이상하거나 사용법이 궁금할 때 아래에 적어주세요.
              <br />
              화면을 캡처해서 함께 보내주시면 훨씬 빨리 확인할 수 있습니다.
            </p>
          </div>
        )}

        {messages.map((m) => {
          const mine = m.author_type === 'owner';
          return (
            <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] ${mine ? 'items-end' : 'items-start'} flex flex-col`}>
                {!mine && (
                  <span className="text-xs text-gray-500 mb-1 px-1">🏢 본부</span>
                )}
                <div
                  className={`rounded-2xl px-4 py-2.5 whitespace-pre-line text-sm leading-relaxed ${
                    mine
                      ? 'bg-blue-600 text-white rounded-br-sm'
                      : 'bg-white border border-gray-200 text-gray-800 rounded-bl-sm'
                  }`}
                >
                  {mine && m.category && (
                    <span className="inline-block text-[11px] bg-blue-500 rounded px-1.5 py-0.5 mb-1">
                      {m.category}
                    </span>
                  )}
                  <div>{m.content}</div>
                  {m.attachments.length > 0 && (
                    <div className="mt-2 space-y-2">
                      {m.attachments.map((a) => (
                        <SupportAttachmentImage
                          key={a.id}
                          id={a.id}
                          filename={a.filename}
                          createdAt={m.created_at}
                          fetchUrl={fetchAttachmentUrl}
                          className="rounded-lg max-h-56 border border-black/10"
                        />
                      ))}
                    </div>
                  )}
                </div>
                <span className="text-[11px] text-gray-400 mt-1 px-1">
                  {formatTime(m.created_at)}
                </span>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {error && (
        <div className="mt-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
          {error}
        </div>
      )}

      {/* 입력 영역 */}
      <div className="mt-3 bg-white border border-gray-200 rounded-lg p-3">
        <div className="flex items-center gap-2 mb-2">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCategory(c)}
              className={`text-xs px-3 py-1 rounded-full border ${
                category === c
                  ? 'bg-blue-50 border-blue-400 text-blue-700 font-semibold'
                  : 'bg-white border-gray-300 text-gray-500'
              }`}
            >
              {c}
            </button>
          ))}
          <label className="ml-auto text-xs text-gray-600 cursor-pointer hover:text-gray-900">
            📷 화면 첨부
            <input
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => addImages(Array.from(e.target.files || []))}
            />
          </label>
        </div>

        {files.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2">
            {files.map((f, i) => (
              <span
                key={i}
                className="text-xs bg-gray-100 rounded px-2 py-1 flex items-center gap-1"
              >
                {f.name}
                <button
                  type="button"
                  className="text-gray-400 hover:text-red-500"
                  onClick={() => setFiles((p) => p.filter((_, idx) => idx !== i))}
                >
                  ✕
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="flex gap-2">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onPaste={handlePaste}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              addImages(Array.from(e.dataTransfer?.files || []));
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) send();
            }}
            rows={2}
            placeholder="내용을 입력하세요 · 화면 캡처는 Ctrl+V 로 바로 붙여넣기 (Ctrl+Enter 전송)"
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
          <button
            type="button"
            onClick={send}
            disabled={sending || !text.trim()}
            className="px-5 rounded-lg bg-blue-600 text-white font-semibold text-sm disabled:bg-gray-300"
          >
            {sending ? '전송중' : '보내기'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SupportChatPage;
